import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ENTITY_TYPE_I18N_KEY } from './watchlistViews';
import type { EntityType } from '@/types/watchlist';

interface ConfirmDeleteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  entityType?: string;
  count?: number;
  isClearAll?: boolean;
}

/**
 * 确认删除弹窗组件
 *
 * 两种场景:
 *   1. 按类型删除:显示 "删除所有 X 类型的关注项 (N 个)"
 *   2. 清空所有:显示 "清空全部"
 *
 * 分组功能已废弃(redesign-watchlist-via-views) — group / displayName /
 * itemCount 等参数已移除。
 */
export function ConfirmDeleteModal({
  open,
  onOpenChange,
  onConfirm,
  entityType,
  count,
  isClearAll = false,
}: ConfirmDeleteModalProps) {
  const { t } = useTranslation('chrome');

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  const entityName = entityType
    ? t(`watchlist.entityType.${ENTITY_TYPE_I18N_KEY[entityType as EntityType] ?? entityType}`)
    : '';

  let description = '';
  if (isClearAll) {
    description = t('watchlist.confirmClearAll');
  } else if (entityType) {
    const c = count ?? 0;
    description = t('watchlist.confirmRemoveType', {
      count: c,
      name: entityName,
      suffix: c !== 1 ? 's' : '',
    });
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('watchlist.confirmTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common:action.cancel', 'Cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} className="bg-red-600 hover:bg-red-700">
            {t('common:action.confirm', 'Confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
